import { AppBar, Toolbar, Container, useMediaQuery, Box, useTheme } from '@mui/material';
import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import NavLogo from './components/NavLogo';
import DesktopNav from './components/DesktopNav';
import MobileNav from './components/MobileNav';
import UserMenu from './components/UserMenu';
import GetLocationModal from '../../getLocationPopup/getLocationPopup';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMyPost } from '../../../redux/post/postSlice';
import { getUserDetails, signOut, getUserAddressObject } from '../../../redux/user/userSlice';
import { API_CALL_RES_STATUS, API_CALL_STATUS } from '../../../assets/constants/constants';
import { useNotification } from '../../../components/shared/NotificationProvider';

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const notify = useNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [openLocationModal, setOpenLocationModal] = useState(false);

  const { loggedInUser, userInfo, myPost, userGeoLocation, geoStatus } = useSelector(state => ({
    loggedInUser: state.auth.loggedInUser,
    userInfo: state.user.userInfo,
    myPost: state.post.myPost,
    userGeoLocation: state.user.userGeoLocation,
    geoStatus: state.user.geoStatus,
  }));

  useEffect(() => {
    if (Object.keys(userInfo).length === 0) {
      dispatch(getUserDetails());
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    dispatch(fetchMyPost());
  }, [dispatch]);

  useEffect(() => {
    if (loggedInUser?.data && !userGeoLocation.coordinates) {
      dispatch(getUserAddressObject());
    }
  }, [userGeoLocation,loggedInUser, dispatch]);

  // Check if user location is available
  useEffect(() => {
    if (!loggedInUser?.data) return;

    // still loading geo → do nothing (prevents flicker)
    if (geoStatus === API_CALL_STATUS.LOADING ||
      geoStatus === API_CALL_STATUS.IDLE) return;

    const hasGeo =
      userGeoLocation &&
      Array.isArray(userGeoLocation.coordinates) &&
      userGeoLocation.coordinates.length > 0;

    setOpenLocationModal(!hasGeo);

  }, [geoStatus, userGeoLocation, loggedInUser]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = (item) => {
    if (item?.path) {
      navigate(item.path);
    }
    setAnchorElNav(null);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = async (item) => {
    if (item?.name === "Logout") {
      const confirmed = await notify.confirm({
        title: "Please confirm",
        message: "Are you sure you want to log out?",
      });

      if (confirmed) {
        try {
          const res = await dispatch(signOut());
          if (res.payload?.status === API_CALL_RES_STATUS.SUCCESS) {
            navigate("/fmr/sign-in");
          } else {
            notify.error(res.payload?.message || "Logout failed");
          }
        } catch (err) {
          notify.error(err?.response?.data?.message || "System level error occurred");
        }
      } else {
        notify.info("Logout cancelled");
      }
    } else if (item?.path) {
      navigate(item.path);
    }
    setAnchorElUser(null);
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          backgroundColor: theme => theme.palette.navbar?.main || '#003027',
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <GetLocationModal 
          openModal={openLocationModal}
          setOpenModal={setOpenLocationModal} 
        />
        
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters 
            sx={{ 
              py: { xs: 1, md: 1.25 },
              gap: 2,
              position: 'relative',
            }}
          >
            <NavLogo isMobile={isMobile} />
            
            {isMobile ? (
              <MobileNav 
                anchorElNav={anchorElNav}
                handleOpenNavMenu={handleOpenNavMenu}
                handleCloseNavMenu={handleCloseNavMenu}
                currentPath={location.pathname}
              />
            ) : (
              <DesktopNav 
                handleNavClick={handleCloseNavMenu} 
                currentPath={location.pathname}
              />
            )}
            
            <Box sx={{ flexGrow: 1 }} />
            
            <UserMenu 
              anchorElUser={anchorElUser}
              handleOpenUserMenu={handleOpenUserMenu}
              handleCloseUserMenu={handleCloseUserMenu}
              userInfo={userInfo}
              hasPost={Object.keys(myPost).length > 0}
              currentPath={location.pathname}
            />
          </Toolbar>
        </Container>
      </AppBar>
      <Outlet />
    </>
  );
};

export default Navbar;
